/**
 * A trade's `action` is product-specific: perp sends the position-centric
 * `OpenLong`/`CloseLong`/`OpenShort`/`CloseShort`, spot sends `Buy`/`Sell`.
 * Both are written from the taker's perspective, so both collapse to a side.
 * `Net` and any unrecognized value have no side and must not be reported as
 * either one.
 *
 * Deliberately dependency-free — it is imported by the orderbook panel as well
 * as the trade history table.
 */

export type ActionSide = "buy" | "sell" | "neutral";

export function getActionSide(action: string): ActionSide {
  switch (action) {
    case "OpenLong":
      return "buy";
    case "CloseLong":
      return "sell";
    case "OpenShort":
      return "sell";
    case "CloseShort":
      return "buy";
    // Spot trade directions
    case "Buy":
      return "buy";
    case "Sell":
      return "sell";
    case "Net":
      return "neutral";
    default:
      return "neutral";
  }
}

export type ClosedPositionSide = "long" | "short";

/**
 * The side of the position a trade closed — the inverse of [getActionSide],
 * which reports the side of the closing *order*. Closing a long is a sell, but
 * the position being shared was a long.
 *
 * `null` for anything that closes no perp position: opens realize no PnL, and
 * spot has no position (nor the leverage the share card's market pill wants).
 */
export function getClosedPositionSide(action: string): ClosedPositionSide | null {
  switch (action) {
    case "CloseLong":
      return "long";
    case "CloseShort":
      return "short";
    default:
      return null;
  }
}

export interface ClosingFillAggregate {
  side: ClosedPositionSide;
  size: number;
  /** Size-weighted price of the closing fills alone. */
  price: number;
}

/**
 * The side, quantity and price at which an order group actually closed,
 * ignoring any opening leg.
 *
 * An order that flips a position through zero emits a close row *and* an open
 * row sharing one `order_id` (`emit_trade_event` in `perp_positions.move`), so
 * the group's first action, summed size and VWAP all describe both legs. Size
 * and price have to exclude the opening leg together: realized PnL is
 * closing-only, so `entry = exit -/+ pnl/size` only holds if both sides of it
 * are measured over the same fills.
 *
 * `null` when the group closes nothing, or closes both directions at once.
 */
export function getClosingFillAggregate(
  fills: ReadonlyArray<{ action: string; size: number; price: number }>,
): ClosingFillAggregate | null {
  let side: ClosedPositionSide | null = null;
  let size = 0;
  let notional = 0;

  for (const fill of fills) {
    const fillSide = getClosedPositionSide(fill.action);
    if (fillSide === null) continue;
    if (side !== null && side !== fillSide) return null;
    side = fillSide;
    size += fill.size;
    notional += fill.price * fill.size;
  }

  return side === null || !(size > 0) ? null : { side, size, price: notional / size };
}

/** A maker rebate is paid to the trader, so it nets as a negative fee. */
export function signedTradeFee(trade: { fee_amount: number; is_rebate: boolean }): number {
  return trade.is_rebate ? -trade.fee_amount : trade.fee_amount;
}

/** Spans both legs of a flip, unlike [getClosingFillAggregate]. */
export function aggregateOrderTrades(
  trades: ReadonlyArray<{ size: number; price: number; fee_amount: number; is_rebate: boolean }>,
): { size: number; avgPrice: number; fee: number } {
  let size = 0;
  let notional = 0;
  let fee = 0;
  for (const trade of trades) {
    size += trade.size;
    notional += trade.price * trade.size;
    fee += signedTradeFee(trade);
  }
  return { size, avgPrice: size > 0 ? notional / size : 0, fee };
}

/**
 * The `PositiveNegative` adapter. `null` — not `undefined` — is what makes the
 * component ignore the row's value and fall through to its neutral color.
 */
export function actionIsPositive(action: string): boolean | null {
  const side = getActionSide(action);
  if (side === "neutral") return null;
  return side === "buy";
}
